import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { QrReader } from 'react-qr-reader';
import ContentArea from '../../components/contentArea';
import Popup from '../../components/popup';
import { isWebView } from '../../utils/base';
import { didimAuthAPI } from '../../actions/imsServer';

export const UserQr = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [isScanning, setIsScanning] = useState(true);
    const [error, setError] = useState(null);
    const [popupOpen, setPopupOpen] = useState(false);
    const [popupMessage, setPopupMessage] = useState('');
    const isMountedRef = useRef(true);
    const abortControllerRef = useRef(null);
    const processedResultsRef = useRef(new Set());
    const isProcessingRef = useRef(false);

    const showPopup = (message) => {
        setPopupMessage(message);
        setPopupOpen(true);
    };

    const handleClosePopup = () => {
        setPopupOpen(false);
    };


    // QR 스캔 결과 처리
    const handleScan = useCallback((data) => {
        if (!data || !isMountedRef.current) return;

        // 이미 처리 중이거나 처리된 결과인지 확인
        if (isProcessingRef.current || processedResultsRef.current.has(data.text)) {
            return;
        }

        console.log('QR Code scanned:', data.text);
        isProcessingRef.current = true;
        processedResultsRef.current.add(data.text);

        const done = () => {
            if (isMountedRef.current) {
                setTimeout(() => {
                    if (isMountedRef.current) isProcessingRef.current = false;
                }, 2000);
            }
        };

        (async () => {
            try {
                let meInfo = null;
                if (typeof localStorage !== 'undefined') {
                    try {
                        const s = localStorage.getItem('meInfo');
                        meInfo = s ? JSON.parse(s) : null;
                    } catch (_) { }
                }
                const userId = meInfo?.id;
                if (!userId) {
                    showPopup(t('an_error_occurred_while_processing_the_qr_code'));
                    done();
                    return;
                }

                let didimAuthData = {};
                try {
                    const parsed = JSON.parse(data.text);
                    if (parsed && (typeof parsed.didimId !== 'undefined' || typeof parsed.didimUid !== 'undefined')) {
                        didimAuthData = { ...parsed, userId };
                    } else {
                        didimAuthData = { didimUid: data.text, userId };
                    }
                } catch {
                    didimAuthData = { didimUid: data.text, userId };
                }

                await didimAuthAPI.registerDidimAuth(didimAuthData);
                if (isMountedRef.current) {
                    showPopup(t('qr_scan_success', { text: data.text }));
                }
            } catch (err) {
                console.error('QR scan processing error:', err);
                if (isMountedRef.current) {
                    showPopup(err?.response?.data?.message || t('an_error_occurred_while_processing_the_qr_code'));
                }
            } finally {
                done();
            }
        })();
    }, [t]);

    const handleError = useCallback((err) => {
        if (!isMountedRef.current) return;

        console.error('QR Reader error:', err);
        console.error('Error details:', {
            name: err?.name,
            message: err?.message,
            stack: err?.stack,
            isWebView: isWebView(),
        });

        // 웹뷰에서 에러 정보 전송
        if (typeof window !== 'undefined' && window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage(
                JSON.stringify({
                    type: 'console',
                    data: {
                        level: 'error',
                        message: `QR Reader error: ${err?.name || 'Unknown'} - ${err?.message || 'No message'}`,
                    },
                })
            );
        }

        setIsScanning(false);

        if (err && err.name) {
            if (err.name === 'NotAllowedError') {
                setError(t('camera_access_permission_is_required'));
            } else if (err.name === 'NotFoundError') {
                setError(t('camera_not_found'));
            } else if (err.name === 'NotReadableError') {
                setError(t('camera_is_in_use_by_another_app'));
            } else {
                setError(t('camera_init_failed_with_name', { name: err.name }));
            }
        } else {
            setError(t('failed_to_initialize_camera'));
        }
    }, [t]);

    // 컴포넌트 마운트/언마운트 관리
    useEffect(() => {
        isMountedRef.current = true;

        return () => {
            isMountedRef.current = false;

            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    // video 요소에 직접 스타일 적용 및 카메라 상태 확인
    useEffect(() => {
        if (!isScanning) return;

        const applyVideoStyles = () => {
            const videoElement = document.getElementById('qr-video');
            if (videoElement) {
                videoElement.style.width = '100vw';
                videoElement.style.height = '100vh';
                videoElement.style.position = 'fixed';
                videoElement.style.top = '0';
                videoElement.style.left = '0';
                videoElement.style.objectFit = 'cover';
                videoElement.style.zIndex = '1';

                // 웹뷰에서 비디오 스트림 상태 확인
                if (isWebView() && videoElement.srcObject) {
                    const stream = videoElement.srcObject;
                    const tracks = stream.getVideoTracks();
                    if (tracks.length > 0) {
                        console.log('Camera track info:', {
                            label: tracks[0].label,
                            enabled: tracks[0].enabled,
                            readyState: tracks[0].readyState,
                            settings: tracks[0].getSettings(),
                        });
                    }
                }
            }
        };

        // 즉시 적용
        applyVideoStyles();

        // QrReader가 video를 생성할 때까지 대기
        const interval = setInterval(() => {
            applyVideoStyles();
        }, 100);

        return () => {
            clearInterval(interval);
        };
    }, [isScanning, isWebView]);

    // QR 리더 설정
    const qrReaderConfig = {
        delay: 300,
        scanDelay: 300,
        resolution: 800,
        audio: false,
        legacyMode: isWebView(), // 웹뷰에서는 legacyMode 사용
        showViewFinder: false,
        showTorchButton: false,
        videoId: 'qr-video',
        constraints: isWebView()
            ? {
                // 웹뷰 환경에서는 더 유연한 설정
                facingMode: { ideal: 'environment' },
                width: { ideal: 640 },
                height: { ideal: 480 },
            }
            : {
                // 일반 브라우저에서는 기존 설정
                facingMode: 'environment',
                width: { min: 320, ideal: 640, max: 1280 },
                height: { min: 240, ideal: 480, max: 720 },
            },
        style: {
            width: '100vw',
            height: '100vh',
            objectFit: 'cover',
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex: 1
        },
        videoStyle: {
            width: '100vw',
            height: '100vh',
            objectFit: 'cover',
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex: 1
        }
    };

    return (
        <>
            <ContentArea
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    width: '100vw',
                    height: '100vh',
                    overflow: 'hidden',
                    padding: 0,
                    margin: 0,
                    backgroundColor: theme.palette.figma.mainBg,
                    zIndex: 0,
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        width: '100%',
                        height: '100%',
                        minHeight: '100vh',
                        backgroundColor: theme.palette.figma.mainBg,
                    }}
                >
                    {/* QR Reader */}
                    {isScanning && (
                        <Box
                            sx={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                width: '100vw',
                                height: '100vh',
                                zIndex: 1,
                                overflow: 'hidden',
                                '& video': {
                                    width: '100vw !important',
                                    height: '100vh !important',
                                    objectFit: 'cover !important',
                                    position: 'fixed !important',
                                    top: '0 !important',
                                    left: '0 !important',
                                    zIndex: '1 !important',
                                },
                            }}
                        >
                            <QrReader
                                onResult={handleScan}
                                onError={handleError}
                                {...qrReaderConfig}
                            />
                        </Box>
                    )}
                    {/* Center content */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 148,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: 200,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '30px',
                            zIndex: 2,
                            backgroundColor: 'transparent',
                            pointerEvents: 'none',
                        }}
                    >
                        <Box
                            sx={{
                                width: 200,
                                height: 200,
                                border: `2px dashed ${theme.palette.figma.white[100]}`,
                                borderRadius: '30px',
                                boxSizing: 'border-box',
                            }}
                        />

                        <Typography
                            sx={{
                                fontSize: 16,
                                fontWeight: 500,
                                color: theme.palette.figma.white[100],
                                textAlign: 'center',
                                letterSpacing: '-0.48px',
                            }}
                        >
                            {t('please_scan_the_didim_screen_qr_code')}
                        </Typography>

                        <IconButton
                            onClick={() => window.history.back()}
                            sx={{
                                width: 40,
                                height: 40,
                                color: theme.palette.figma.white[100],
                                backgroundColor: theme.palette.figma.white[20],
                                pointerEvents: 'auto',
                                '&:hover': {
                                    backgroundColor: theme.palette.figma.white[30],
                                },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* Error message */}
                    {error && (
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 100,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                zIndex: 2,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: 14,
                                    color: theme.palette.error.main,
                                    textAlign: 'center',
                                    backgroundColor: theme.palette.figma.white[70],
                                    padding: '10px 20px',
                                    borderRadius: '8px',
                                }}
                            >
                                {error}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </ContentArea>

            <Popup
                open={popupOpen}
                onConfirm={handleClosePopup}
                message={popupMessage}
            />
        </>
    );
};

export default UserQr;
